// When did this visitor land? — the single answer behind the monitor's "on
// site" timer and its default (newest-arrival-first) ordering.
//
// The agent app used to stamp `Date.now()` the first time it heard a presence
// heartbeat for a visitor. For anyone already on the site when the agent
// logged in, that is the AGENT's login time, and every row in the monitor read
// the same arrival — the bug this module exists to fix.
//
// The widget now reports the elapsed time of the visitor's CURRENT visit
// (`context.timeOnSite`, see packages/widget/src/visit.ts): it spans page
// navigation within the site, and a visitor who comes back after being away
// starts a fresh visit. Subtracting it from the heartbeat's own timestamp
// gives a real arrival on the same clock the row's `seenAt` already uses, so a
// skewed visitor clock cannot turn the timer into nonsense.

/** Longest visit we'll believe a widget's reported elapsed time for. */
const MAX_VISIT_MS = 12 * 60 * 60_000;

/**
 * How far a heartbeat's reported arrival may sit from the one we already hold
 * before we accept it as a correction. Emit timing and network latency move
 * the reported value by a few hundred ms per beat, and `arrivedAt` is the
 * default monitor sort key — rewriting it every 10s would jitter the order of
 * visitors who landed at nearly the same moment.
 */
const ARRIVAL_DRIFT_MS = 5_000;

/**
 * The arrival the reporting widget claims, or null when it didn't say (an
 * older cached build) or said something that cannot be true.
 *
 * @param at        the heartbeat's timestamp — the clock `seenAt` also uses
 * @param timeOnSite  ms the widget says the visitor has been on site this visit
 */
export function reportedArrival(at: number, timeOnSite: unknown): number | null {
  if (typeof timeOnSite !== 'number' || !Number.isFinite(timeOnSite)) return null;
  if (timeOnSite <= 0 || timeOnSite > MAX_VISIT_MS) return null;
  return at - timeOnSite;
}

/**
 * Resolve `arrivedAt` for a presence heartbeat.
 *
 * @param held      the arrival currently on the row, or null for a new row
 * @param claimed   `reportedArrival(...)` for this heartbeat
 * @param at        the heartbeat's timestamp
 * @param opts.departed  this heartbeat says the visitor left — never re-arrive
 *                       them (a stray offline ping must not bump the row)
 * @param opts.freshArrival  the row was offline (departed, seeded from history,
 *                       or its heartbeat lapsed) and is coming back now
 */
export function resolveArrival(
  held: number | null,
  claimed: number | null,
  at: number,
  opts: { departed: boolean; freshArrival: boolean },
): number {
  if (held === null) return claimed ?? at;
  if (opts.departed) return held;
  if (claimed === null) return opts.freshArrival ? at : held;
  // A live heartbeat keeps reporting the visit it belongs to, so a row we
  // first met mid-visit converges on the visitor's real arrival — but only
  // when the two genuinely disagree, so per-beat jitter leaves the order be.
  return Math.abs(claimed - held) > ARRIVAL_DRIFT_MS ? claimed : held;
}
